import jobServices from 'ApiService/Admin/jobServices';
import studentService from 'ApiService/Admin/studentService';
import branchService from 'ApiService/SuperAdmin/branchService';
import Card from 'components/card';
import { AuthContext } from 'contexts/AuthContext';
import ConfirmationModal from 'pages/Common/ConfirmationModal';
import React, { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { BallTriangle } from 'react-loader-spinner';

const PausedStudentsTable = ({ classData }) => {
  const { state } = useContext(AuthContext);
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [pauseTypeFilter, setPauseTypeFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [unpauseReason, setUnpauseReason] = useState('');
  const [showUnpauseModal, setShowUnpauseModal] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [confirmationLoading, setConfirmationLoading] = useState(false);
  const selectedBranch = state?.selectedBranch || '';
  // Super admins unpause directly instead of raising a request
  const isSuperAdmin = state?.user?.role === 'superadmin';
  // Admins can resume auto-declined pauses without approval
  const canUnpauseDirectly = (student) =>
    isSuperAdmin || !!student?.pauseInfo?.reason?.startsWith('Auto Declined');

  const toggleRow = (studentId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [studentId]: !prev[studentId],
    }));
  };

  useEffect(() => {
    fetchPausedStudents();
  }, [classData.courseId, classData.batchId, selectedBranch]);

  useEffect(() => {
    let result = [...students];

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter((student) => {
        return (
          (student.name && student.name.toLowerCase().includes(searchLower)) ||
          (student.courseName &&
            student.courseName.toLowerCase().includes(searchLower)) ||
          (student.batchNumber &&
            String(student.batchNumber).toLowerCase().includes(searchLower))
        );
      });
    }

    if (pauseTypeFilter !== 'all') {
      result = result.filter((student) =>
        pauseTypeFilter === 'permanent'
          ? student.pauseInfo.isPermanent
          : !student.pauseInfo.isPermanent
      );
    }

    if (sortOrder) {
      result.sort((a, b) => {
        const compareValue = a.name.localeCompare(b.name);
        return sortOrder === 'asc' ? compareValue : -compareValue;
      });
    }

    setFilteredStudents(result);
  }, [students, pauseTypeFilter, sortOrder, searchTerm]);

  const fetchPausedStudents = async () => {
    try {
      setLoading(true);

      const response = await studentService.getAllPausedStudents(
        classData.courseId,
        classData.batchId,
        selectedBranch
      );
      if (response.data.success) {
        setStudents(response?.data?.data || []);

        if (response.data.data.length === 0) {
          toast.error('No paused students found.');
        }
      }
    } catch (err) {
      console.error('Failed to fetch paused students:', err);
      setStudents([]);
      toast.error(err?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setSearchTerm('');
    setPauseTypeFilter('all');
    setSortOrder('');
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleRequestUnpausePlacement = (student) => {
    setSelectedStudent(student);
    setUnpauseReason('');
    setShowUnpauseModal(true);
  };

  const submitUnpauseRequest = async () => {
    setConfirmationLoading(true);
    try {
      if (canUnpauseDirectly(selectedStudent)) {
        const response = isSuperAdmin
          ? await branchService.unpausePlacement(
              selectedStudent._id,
              unpauseReason.trim()
            )
          : await jobServices.unpausePlacement(
              selectedStudent._id,
              unpauseReason.trim()
            );

        if (response.data.success) {
          toast.success(response.data.message);
          setShowUnpauseModal(false);
          setIsConfirmationModalOpen(false);
          fetchPausedStudents();
        } else {
          toast.error(response.data.message);
        }
        return;
      }

      const response = await jobServices.requestUnpausePlacement(
        selectedStudent._id,
        unpauseReason.trim()
      );

      if (response.data.success) {
        toast.success(response.data.message);

        // Update the student's status in the local state
        setStudents((prev) =>
          prev.map((student) =>
            student._id === selectedStudent._id
              ? {
                  ...student,
                  currentUnpauseRequestStatus: 'pending',
                }
              : student
          )
        );
        setFilteredStudents((prev) =>
          prev.map((student) =>
            student._id === selectedStudent._id
              ? {
                  ...student,
                  currentUnpauseRequestStatus: 'pending',
                }
              : student
          )
        );

        setShowUnpauseModal(false);
        setIsConfirmationModalOpen(false);
        fetchPausedStudents();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to submit unpause request'
      );
      // The list may be stale (e.g. student already unpaused elsewhere)
      setShowUnpauseModal(false);
      setIsConfirmationModalOpen(false);
      fetchPausedStudents();
    } finally {
      setConfirmationLoading(false);
    }
  };

  const confirmUnpausePlacementRequest = () => {
    if (!unpauseReason.trim()) {
      toast.error('Please provide a reason for unpause request');
      return;
    }

    setIsConfirmationModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex h-96 w-full items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <BallTriangle color="#5431ed" />
          <p className="text-sm text-gray-500">Loading student data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Search and Filter Section */}
      <div className="mt-2 flex flex-col items-center gap-4 sm:flex-row">
        {/* Search Input */}
        <div className="w-full rounded-xl border border-gray-200 bg-lightPrimary py-2.5 pl-2.5 pr-3 text-sm font-normal text-gray-600 outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white sm:w-full md:w-full">
          <input
            type="text"
            placeholder="Search by Student Name, Course Name, or Batch Number"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full rounded-full bg-lightPrimary text-base text-navy-700 outline-none dark:!bg-navy-800 dark:text-white"
          />
        </div>

        {/* Pause Type Filter */}
        <div className="w-full sm:w-[300px]">
          <div className="w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal text-gray-600 outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white sm:w-full md:w-full">
            <select
              className="text-lightFourth w-full rounded-xl pr-4 text-sm outline-none dark:!bg-navy-800 dark:text-white"
              value={pauseTypeFilter}
              onChange={(e) => setPauseTypeFilter(e.target.value)}
            >
              <option value="all">All Pause Types</option>
              <option value="permanent">Permanent</option>
              <option value="temporary">Temporary</option>
            </select>
          </div>
        </div>

        {/* Sort Order */}
        <div className="w-full sm:w-[300px]">
          <div className="w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal text-gray-600 outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white sm:w-full md:w-full">
            <select
              className="text-lightFourth w-full rounded-xl pr-4 text-sm outline-none dark:!bg-navy-800 dark:text-white"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <option value="">Sort Order</option>
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>
        </div>

        {/* Reset Filters Button */}
        <div className="w-full sm:w-[200px]">
          <button
            onClick={resetFilters}
            className="w-40 rounded-xl bg-brand-500 py-2.5 pl-2 pr-3 text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
          >
            Reset Filters
          </button>
        </div>
      </div>

      {/* Students Table */}
      {filteredStudents.length === 0 ? (
        <div className="mt-4 flex h-96 w-full items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 dark:bg-navy-700">
          <div className="flex flex-col items-center gap-2 p-4 text-center">
            <div className="rounded-full bg-gray-100 p-3">
              <svg
                className="h-6 w-6 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                />
              </svg>
            </div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-white">
              No students found
            </h3>
            <p className="text-sm text-gray-500">
              No students match the current search and filter criteria.
            </p>
          </div>
        </div>
      ) : (
        <div className="mt-4 w-full">
          <h4 className="mb-2 text-lg font-bold text-navy-700 dark:text-white">
            Paused Student List:
          </h4>

          <div className="w-full overflow-x-auto">
            <table className="w-full table-fixed border-collapse bg-white dark:bg-navy-800">
              <thead className="bg-gray-50 dark:border-b dark:border-gray-800 dark:bg-navy-900">
                <tr>
                  <th className="w-10 px-4 py-3"></th>
                  <th className="w-1/7 px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                    Student Name
                  </th>
                  <th className="w-1/5 px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                    Course
                  </th>
                  <th className="w-1/7 px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white">
                    Batch
                  </th>
                  <th className="w-1/5 px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                    Current Pause Reason
                  </th>
                  <th className="w-1/7 px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white">
                    Start Date
                  </th>
                  <th className="w-1/7 px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white">
                    Status
                  </th>
                  <th className="w-1/7 px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredStudents.map((student) => (
                  <React.Fragment key={student._id}>
                    <tr className="hover:bg-gray-50 dark:bg-navy-900 hover:dark:bg-navy-900">
                      <td className="w-10 px-4 py-3">
                        <button
                          onClick={() => toggleRow(student._id)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          {expandedRows[student._id] ? '▼' : '▶'}
                        </button>
                      </td>
                      <td className="w-1/7 px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                        {student.name}
                      </td>
                      <td className="w-1/5 overflow-hidden truncate whitespace-nowrap px-4 py-3 text-sm text-navy-800 dark:text-white/60">
                        {student.courseName}
                      </td>
                      <td className="w-1/7 px-4 py-3 text-center text-sm text-navy-800 dark:text-white/60">
                        {student.batchNumber}
                      </td>
                      <td className="w-1/5 px-4 py-3 text-sm text-navy-800 dark:text-white/60">
                        {student.pauseInfo.reason}
                      </td>
                      <td className="w-1/7 px-4 py-3 text-center text-sm text-navy-800 dark:text-white/60">
                        {formatDate(student.pauseInfo.startDate)}
                      </td>
                      <td className="w-1/7 px-4 py-3 text-center">
                        <span
                          className={`rounded-full px-2 py-1 text-xs ${
                            student.pauseInfo.isPermanent
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {student.pauseInfo.isPermanent
                            ? 'Permanent'
                            : 'Temporary'}
                        </span>
                        {student.pauseInfo.selfPaused && (
                          <span className="mt-1 inline-block rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-800">
                            Paused by Student
                          </span>
                        )}
                      </td>
                      <td className="w-1/7 px-4 py-3 text-center">
                        {!student.pauseInfo.isPermanent && (
                          <div>
                            {student.pauseInfo.selfPaused ? (
                              // Self pauses wait for the student's request;
                              // only super admins (and head HRs) act on it
                              student.status === 'pending' ? (
                                isSuperAdmin ? (
                                  <button
                                    onClick={() =>
                                      handleRequestUnpausePlacement(student)
                                    }
                                    className="rounded bg-blue-500 px-2 py-1 text-xs text-white hover:bg-blue-600"
                                  >
                                    Unpause Placement
                                  </button>
                                ) : (
                                  <span className="rounded-full bg-yellow-100 px-2 py-1 text-xs text-yellow-800">
                                    Unpause Requested
                                  </span>
                                )
                              ) : (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  Waiting for student request
                                </span>
                              )
                            ) : canUnpauseDirectly(student) ? (
                              <button
                                onClick={() =>
                                  handleRequestUnpausePlacement(student)
                                }
                                className="rounded bg-blue-500 px-2 py-1 text-xs text-white hover:bg-blue-600"
                              >
                                Unpause Placement
                              </button>
                            ) : student.status === 'pending' ? (
                              <span className="rounded-full bg-yellow-100 px-2 py-1 text-xs text-yellow-800">
                                Pending Approval
                              </span>
                            ) : student.status === 'approved' ? (
                              <span className="rounded-full bg-green-100 px-2 py-1 text-xs text-green-800">
                                Approved
                              </span>
                            ) : student.status === 'rejected' ? (
                              <div className="flex items-center gap-2">
                                <span className="rounded-full bg-red-100 px-2 py-1 text-xs text-red-800">
                                  Rejected
                                </span>
                                <button
                                  onClick={() =>
                                    handleRequestUnpausePlacement(student)
                                  }
                                  className="rounded bg-blue-500 px-2 py-1 text-xs text-white hover:bg-blue-600"
                                >
                                  Retry Request
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() =>
                                  handleRequestUnpausePlacement(student)
                                }
                                className="rounded bg-green-500 px-2 py-1 text-xs text-white hover:bg-green-600"
                              >
                                Request Unpause
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                    {expandedRows[student._id] && (
                      <tr>
                        <td
                          colSpan={8}
                          className="bg-gray-50 px-4 py-4 dark:bg-navy-900"
                        >
                          <Card className="dark:border-gray-800">
                            <div className="p-4">
                              <h4 className="mb-4 text-sm font-semibold dark:text-white">
                                Pause History
                              </h4>
                              <div className="space-y-4">
                                {student.pauseInfo?.allPauseReasons?.map(
                                  (reason, index) => (
                                    <div
                                      key={index}
                                      className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-navy-900"
                                    >
                                      <div className="flex items-center justify-between">
                                        <div className="space-y-1">
                                          <p className="text-sm font-medium dark:text-white">
                                            Reason: {reason}
                                          </p>
                                          {index === 0 && (
                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                              Current pause:{' '}
                                              {formatDate(
                                                student.pauseInfo.startDate
                                              )}{' '}
                                              -{' '}
                                              {student.pauseInfo.isPermanent
                                                ? 'Permanent'
                                                : formatDate(
                                                    student.pauseInfo.endDate
                                                  ) || 'Ongoing'}
                                            </p>
                                          )}
                                        </div>
                                        {index === 0 && (
                                          <span
                                            className={`rounded-full px-2 py-1 text-xs ${
                                              student.pauseInfo.isPermanent
                                                ? 'bg-red-100 text-red-800'
                                                : 'bg-yellow-100 text-yellow-800'
                                            }`}
                                          >
                                            {student.pauseInfo.isPermanent
                                              ? 'Permanent'
                                              : 'Temporary'}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  )
                                )}
                              </div>
                              {student.pauseInfo?.allUnpauseReasons?.length >
                                0 && (
                                <>
                                  <h4 className="mb-4 mt-6 text-sm font-semibold dark:text-white">
                                    Unpause Reasons
                                  </h4>
                                  <div className="space-y-4">
                                    {student.pauseInfo?.allUnpauseReasons?.map(
                                      (unpauseReason, index) => (
                                        <div
                                          key={index}
                                          className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-navy-900"
                                        >
                                          <p className="text-sm font-medium dark:text-white">
                                            Unpause Reason: {unpauseReason}
                                          </p>
                                        </div>
                                      )
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          </Card>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Unpause Modal */}
      {showUnpauseModal && (
        <div className="bg-black fixed inset-0 z-50 flex items-center justify-center bg-opacity-50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-navy-800">
            <h3 className="mb-4 text-lg font-semibold dark:text-white">
              {canUnpauseDirectly(selectedStudent)
                ? 'Unpause Placement'
                : 'Request Unpause Placement'}
            </h3>
            <textarea
              value={unpauseReason}
              onChange={(e) => setUnpauseReason(e.target.value)}
              placeholder={
                canUnpauseDirectly(selectedStudent)
                  ? 'Provide reason for unpausing'
                  : 'Provide reason for unpause request'
              }
              className="mb-4 w-full rounded border p-2 dark:bg-navy-700 dark:text-white"
              rows={4}
            />
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowUnpauseModal(false)}
                className="rounded border px-4 py-2 hover:bg-gray-100 dark:text-white"
              >
                Cancel
              </button>
              <button
                onClick={confirmUnpausePlacementRequest}
                className="rounded bg-green-500 px-4 py-2 text-white hover:bg-green-600"
              >
                {canUnpauseDirectly(selectedStudent)
                  ? 'Unpause'
                  : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={() => {
          if (!confirmationLoading) setIsConfirmationModalOpen(false);
        }}
        onConfirm={submitUnpauseRequest}
        title={
          canUnpauseDirectly(selectedStudent)
            ? 'Confirm Unpause'
            : 'Confirm Unpause Request'
        }
        message={
          canUnpauseDirectly(selectedStudent)
            ? 'Are you sure you want to unpause placement for this student?'
            : 'Are you sure you want to submit an unpause request for this student?'
        }
        confirmText={
          confirmationLoading
            ? 'Processing...'
            : canUnpauseDirectly(selectedStudent)
            ? 'Yes, Unpause'
            : 'Yes, Submit'
        }
        cancelText="Cancel"
        icon="info"
        disableConfirm={confirmationLoading}
      />
    </div>
  );
};

export default PausedStudentsTable;
